/**
 * useMyVector / useTeamVector — the React bindings for the Personal Home and
 * Team Home.
 *
 * Strict layering: pages call these hooks; the hooks build the pure home
 * view-models ({@link buildMyVector} / {@link buildTeamVector}) over the shared
 * {@link useContextSnapshot} (which loads every entity through the feature
 * stores / api facade). To surface "upcoming milestones" — which live in the
 * goal S3 body, not the pointer-row summary — the hooks additionally hydrate
 * the relevant Goal documents via {@link GoalStore} and collect their dated
 * milestones CLIENT-SIDE. No new backend, no new collection.
 */

import { useEffect, useMemo, useState } from 'react';
import { useApi } from '../../hooks/useApi';
import { useContextSnapshot } from '../intelligence/useContextSnapshot';
import { GoalStore } from '../goals/goalStore';
import {
  buildMyVector,
  buildTeamVector,
  collectUpcomingMilestones,
  type MilestoneItem,
  type MyVectorModel,
  type TeamVectorModel,
} from './homeModel';

/**
 * Hydrates the given goal ids and collects their upcoming/overdue milestones.
 * Bounded by the id set passed in (my goals for the personal home; all goals
 * for the team home). Re-runs only when the id set changes.
 */
function useGoalMilestones(goalIds: string[]): MilestoneItem[] {
  const api = useApi();
  const store = useMemo(() => new GoalStore(api), [api]);
  const [milestones, setMilestones] = useState<MilestoneItem[]>([]);
  const key = useMemo(() => goalIds.slice().sort().join(','), [goalIds]);

  useEffect(() => {
    let cancelled = false;
    const ids = key ? key.split(',') : [];
    if (ids.length === 0) {
      setMilestones([]);
      return;
    }
    void (async () => {
      try {
        const docs = await Promise.all(ids.map((id) => store.get(id)));
        if (cancelled) return;
        const withMilestones = docs
          .filter((g): g is NonNullable<typeof g> => !!g && !g.deleted)
          .map((g) => ({
            id: g.id,
            title: g.title,
            status: g.status,
            nextMilestone: g.nextMilestone,
          }));
        setMilestones(collectUpcomingMilestones(withMilestones));
      } catch {
        if (!cancelled) setMilestones([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [key, store]);

  return milestones;
}

export interface UseMyVectorResult {
  model: MyVectorModel | null;
  milestones: MilestoneItem[];
  alias: string;
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
}

export function useMyVector(): UseMyVectorResult {
  const { snapshot, currentAlias, loading, error, refresh } = useContextSnapshot();

  const model = useMemo(
    () => (snapshot && currentAlias ? buildMyVector(snapshot, currentAlias) : null),
    [snapshot, currentAlias],
  );

  const goalIds = useMemo(() => (model ? model.goals.map((g) => g.id) : []), [model]);
  const milestones = useGoalMilestones(goalIds);

  return { model, milestones, alias: currentAlias, loading, error, refresh };
}

export interface UseTeamVectorResult {
  model: TeamVectorModel | null;
  milestones: MilestoneItem[];
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
}

export function useTeamVector(): UseTeamVectorResult {
  const { snapshot, loading, error, refresh } = useContextSnapshot();

  const model = useMemo(
    () => (snapshot ? buildTeamVector(snapshot) : null),
    [snapshot],
  );

  const goalIds = useMemo(
    () => (snapshot ? snapshot.goals.map((g) => g.id) : []),
    [snapshot],
  );
  const milestones = useGoalMilestones(goalIds);

  return { model, milestones, loading, error, refresh };
}
